import React, { useMemo, useContext } from 'react'

import { Button, Form, Paragraph } from '@newComponents'
import { YStack, XStack, Stack } from 'tamagui'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import heartIcon from '@newComponents/EmojiIcon/images/red-heart.png'

import { DialogContext } from '@newComponents/Dialog/Dialog'

import * as Yup from 'yup'

export const GuildDonationAmountForm = ({
  minimumDonationAmount,
  defaultDonationAmount,
  donationCurrency,
  onSubmit,
}: {
  minimumDonationAmount: number
  defaultDonationAmount?: number
  donationCurrency: string
  onSubmit: (donationAmount: number) => Promise<any>
}) => {
  const dialogContext = useContext(DialogContext)

  const validationSchema = useMemo(() => {
    const minimumAmountFormatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: donationCurrency,
      maximumFractionDigits: 0,
    }).format(minimumDonationAmount / 100)

    return Yup.object().shape({
      amount: Yup.number()
        .required('Required')
        .min(
          minimumDonationAmount / 100,
          `Needs to be at least ${minimumAmountFormatted}`
        ),
    })
  }, [minimumDonationAmount, donationCurrency])

  return (
    <>
      <Form
        initialValues={{
          amount: defaultDonationAmount
            ? defaultDonationAmount / 100
            : minimumDonationAmount / 100,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          await onSubmit(values.amount * 100)
        }}
      >
        <Form.Field name='amount' label='Donation amount' flex={1}>
          {dialogContext.open === undefined ? (
            <Stack
              flexDirection='column'
              gap='$2'
              $gtSm={{ flexDirection: 'row', gap: '$8', flex: 1 }}
            >
              <Form.CurrencyInput
                intlConfig={{ locale: 'en-US', currency: donationCurrency }}
                min={minimumDonationAmount / 100}
                // @ts-ignore size Token conflicts with HTML input size attribute
                size='$6'
                maxWidth='100%'
                flex={1}
              />

              <Form.SubmitButton>
                <Form.SubmitButton.Text>Next</Form.SubmitButton.Text>
              </Form.SubmitButton>
            </Stack>
          ) : (
            <XStack gap='$8' flex={1}>
              <Form.CurrencyInput
                intlConfig={{ locale: 'en-US', currency: donationCurrency }}
                min={minimumDonationAmount / 100}
                // @ts-ignore size Token conflicts with HTML input size attribute
                size='$6'
                maxWidth='100%'
                flex={1}
              />

              <Form.SubmitButton>
                <Form.SubmitButton.Text>Next</Form.SubmitButton.Text>
              </Form.SubmitButton>
            </XStack>
          )}
        </Form.Field>

        <YStack gap='$2' alignItems='flex-end'>
          <XStack alignItems='center' gap='$2'>
            <Paragraph
              size='$4'
              color='$color'
              textAlign='right'
              maxWidth={200}
            >
              Thank you for your support!
            </Paragraph>

            <EmojiIcon size='$4' src={heartIcon} />
          </XStack>

          <Paragraph size='$4' color='$colorSubtle' textAlign='right' maxWidth={200}>
            Donations are non-refundable
          </Paragraph>
        </YStack>
      </Form>
    </>
  )
}
